import { QueryError } from '@graphql-box/helpers';
import { Clear } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { type Movie, type ScreenType, type Tv } from '@tmdb-graphql-api/schema/types';
import { type ReactElement, useEffect } from 'react';
import { PosterImage } from '#components/PosterImage/index.tsx';
import { TileCarousel } from '#components/TileCarousel/index.tsx';
import { mapSectionTypeToScreenType } from '#helpers/enumMappers.ts';
import { formatAndMergeMoviesAndTvResults } from '#helpers/formatAndMergeMoviesAndTvResults.ts';
import { useMovieTvMultiQuery } from '#hooks/useMovieTvMultiQuery.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';
import { type ActivePreview } from '#types.ts';

export type RecentlyWatchedCarouselProps = {
  children?: (element?: ReactElement) => ReactElement | undefined;
  setActivePreview?: (item: ActivePreview) => void;
};

export const RecentlyWatchedCarousel = ({ children, setActivePreview }: RecentlyWatchedCarouselProps) => {
  const { config } = useToolbelt();
  const recentlyWatchedMovies = useStore(store => store.recentlyWatchedMovies);
  const recentlyWatchedTv = useStore(store => store.recentlyWatchedTv);
  const removeRecentlyWatched = useStore(store => store.removeRecentlyWatched);

  const { execute, movieLoading, movieResults, tvLoading, tvResults } = useMovieTvMultiQuery({
    initiator: 'RecentlyWatchedCarousel',
  });

  const depsKey = JSON.stringify({ movies: recentlyWatchedMovies, tv: recentlyWatchedTv });

  useEffect(() => {
    execute(recentlyWatchedMovies, recentlyWatchedTv);
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  const movieData: Movie[] = [];
  const tvData: Tv[] = [];
  const errors: QueryError[] = [];

  for (const result of movieResults) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      movieData.push(result.data.movie);
    }
  }

  for (const result of tvResults) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      tvData.push(result.data.tv);
    }
  }

  if (errors.length > 0) {
    console.log(errors);
  }

  const items = formatAndMergeMoviesAndTvResults(movieData, tvData, config);

  if (!movieLoading && !tvLoading && items.length === 0) {
    return;
  }

  const handleRemove = (id: string, type: ScreenType) => () => {
    removeRecentlyWatched(id, type);
  };

  const tileCarousel = (
    <TileCarousel
      items={items}
      renderImage={props => (
        <PosterImage
          {...props}
          renderAdditionalOptionItems={() => (
            // @ts-expect-error id is always a string
            // eslint-disable-next-line react/prop-types
            <MenuItem onClick={handleRemove(props.id, mapSectionTypeToScreenType(props.type))}>
              <ListItemIcon>
                <Clear fontSize="small" />
              </ListItemIcon>
              <ListItemText>Remove recently watched</ListItemText>
            </MenuItem>
          )}
        />
      )}
      setActivePreview={setActivePreview}
    />
  );

  return children ? children(tileCarousel) : tileCarousel;
};
